import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Building, Search, Ticket, User, X } from 'lucide-react';
import { EmptyState, SkeletonRow, StatusPill } from '@/components/ui';
import { api } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';

interface SearchResult {
  tickets: {
    id: string;
    ticketNumber: number;
    subject: string;
    status: string;
    priority: string;
    requester: { firstName: string; lastName: string; email: string } | null;
  }[];
  contacts: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  }[];
  companies: { id: string; name: string; domain: string | null }[];
  articles: { id: string; title: string }[];
}

/**
 * Lets chrome outside this component (the header search field) open the
 * overlay without lifting `open` into a context just for one trigger.
 */
const OPEN_SEARCH_EVENT = 'suzukidesk:open-search';

export function openGlobalSearch() {
  document.dispatchEvent(new Event(OPEN_SEARCH_EVENT));
}

export function GlobalSearch() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState<SearchResult | null>(null);
  const [loading, setLoading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>();
  const searchAbortRef = React.useRef<AbortController | null>(null);

  // '/' keyboard shortcut to open
  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    function handleOpenRequest() {
      setOpen(true);
    }
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener(OPEN_SEARCH_EVENT, handleOpenRequest);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener(OPEN_SEARCH_EVENT, handleOpenRequest);
    };
  }, []);

  React.useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  function handleSearch(value: string) {
    setQuery(value);
    clearTimeout(debounceRef.current);
    searchAbortRef.current?.abort();
    if (value.length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      searchAbortRef.current = controller;
      try {
        const res = await api.get<{ data: SearchResult }>('/tickets/search', {
          params: { q: value },
          signal: controller.signal,
        });
        if (!controller.signal.aborted) setResults(res.data);
      } catch (error) {
        if (!(error instanceof Error && error.name === 'AbortError')) setResults({ tickets: [], contacts: [], companies: [], articles: [] });
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
  }

  React.useEffect(() => () => {
    clearTimeout(debounceRef.current);
    searchAbortRef.current?.abort();
  }, []);

  function navigateTo(path: string) {
    setOpen(false);
    setQuery('');
    setResults(null);
    navigate(path);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-search flex items-start justify-center pt-[15vh]" role="dialog" aria-label={t('search.dialogAria')}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-ink/50" onClick={() => setOpen(false)} aria-hidden="true" />

      {/* Search panel */}
      <div className="relative w-full max-w-2xl rounded-xl border bg-surface shadow-overlay">
        <div className="flex items-center border-b px-4">
          <Search className="h-5 w-5 shrink-0 text-ink-subtle" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={t('search.placeholder')}
            className="flex-1 bg-transparent px-3 py-4 text-base outline-none placeholder:text-ink-subtle"
            aria-label={t('search.aria')}
          />
          <button type="button" onClick={() => setOpen(false)} className="text-ink-subtle hover:text-ink-muted" aria-label={t('search.close')}>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {loading && <SkeletonRow rows={4} />}

          {!loading && results && results.tickets.length === 0 && results.contacts.length === 0 && (!results.companies || results.companies.length === 0) && (!results.articles || results.articles.length === 0) && (
            <EmptyState
              icon={Search}
              title={t('search.noMatches', { query })}
              description={t('search.noMatchesHint')}
              className="py-10"
            />
          )}

          {!loading && results && results.tickets.length > 0 && (
            <div className="p-2">
              <p className="px-2 py-1 text-xs font-medium uppercase text-ink-subtle">{t('nav.tickets')}</p>
              {results.tickets.map((t) => {
                return (
                  <button
                    key={t.id}
                    onClick={() => navigateTo(`/tickets/${t.id}`)}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-surface-hover"
                  >
                    <Ticket className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <span className="font-mono text-xs text-ink-subtle">#{t.ticketNumber}</span>{' '}
                      <span className="truncate text-sm font-medium">{t.subject}</span>
                    </div>
                    <StatusPill value={t.status} className="shrink-0 text-[10px]" />
                  </button>
                );
              })}
            </div>
          )}

          {!loading && results && results.contacts.length > 0 && (
            <div className="border-t p-2">
              <p className="px-2 py-1 text-xs font-medium uppercase text-ink-subtle">{t('nav.contacts')}</p>
              {results.contacts.map((c) => (
                <button
                  key={c.id}
                  onClick={() => navigateTo(`/contacts?search=${c.email}`)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-surface-hover"
                >
                  <User className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <span className="text-sm font-medium">
                      {c.firstName} {c.lastName}
                    </span>
                    <span className="ml-2 text-xs text-ink-subtle">{c.email}</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {!loading && results && results.companies && results.companies.length > 0 && (
            <div className="border-t p-2">
              <p className="px-2 py-1 text-xs font-medium uppercase text-ink-subtle">{t('nav.companies')}</p>
              {results.companies.map((c) => (
                <button
                  key={c.id}
                  onClick={() => navigateTo(`/companies/${c.id}`)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-surface-hover"
                >
                  <Building className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <span className="text-sm font-medium">{c.name}</span>
                    {c.domain && <span className="ml-2 text-xs text-ink-subtle">{c.domain}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}

          {!loading && results && results.articles && results.articles.length > 0 && (
            <div className="border-t p-2">
              <p className="px-2 py-1 text-xs font-medium uppercase text-ink-subtle">{t('nav.knowledgeBase')}</p>
              {results.articles.map((a) => (
                <button
                  key={a.id}
                  onClick={() => navigateTo(`/portal/kb/${a.id}`)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-surface-hover"
                >
                  <BookOpen className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                  <span className="text-sm font-medium">{a.title}</span>
                </button>
              ))}
            </div>
          )}

          {!results && !loading && (
            <div className="px-4 py-6 text-center text-sm text-ink-subtle">
              {t('search.hintBefore')}{' '}
              <kbd className="rounded border bg-surface-subtle px-1.5 py-0.5 font-mono text-xs">Esc</kbd>{' '}
              {t('search.hintAfter')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
